import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import AsyncSelect from 'react-select/async';
import api from '../../services/api';
import toast from 'react-hot-toast';
import Modal from '../../components/common/Modal';
import Table from '../../components/common/Table';
import Pagination from '../../components/common/Pagination';
import { LuPlus, LuPencil, LuTrash2, LuSearch, LuUsers } from 'react-icons/lu';
import { useAuth } from '../../context/AuthContext';
import { debounce, selectStyles } from '../../utils/selectConfig';

const EMPTY = { name: '', phone: '', email: '', company: '' };

export default function Employees() {
  const { isAdmin } = useAuth();
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState(null);
  const PAGE_SIZE = 10;
  const [modal, setModal] = useState({ open: false, mode: 'add', data: null });
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [companyOpt, setCompanyOpt] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, limit: PAGE_SIZE });
      if (search) params.set('search', search);
      const { data } = await api.get(`/employees?${params}`);
      setEmployees(data.data || []);
      setPagination(data.pagination || null);
    } catch { toast.error('Failed to load agents'); }
    finally { setLoading(false); }
  }, [page, search]);

  useEffect(() => { load(); }, [load]);

  const loadCompanies = useMemo(() => debounce(async (q) => {
    const { data } = await api.get(`/companies?search=${encodeURIComponent(q)}&limit=10`);
    return (data.data || []).map(c => ({ value: c._id, label: c.name }));
  }, 300), []);

  const set = k => e => setForm(f => ({ ...f, [k]: e.target.value }));

  const openAdd = () => {
    setForm(EMPTY); setCompanyOpt(null);
    setModal({ open: true, mode: 'add', data: null });
  };
  const openEdit = r => {
    setForm({ name: r.name, phone: r.phone, email: r.email || '', company: r.company?._id || r.company || '' });
    setCompanyOpt(r.company?._id ? { value: r.company._id, label: r.company.name } : null);
    setModal({ open: true, mode: 'edit', data: r });
  };
  const close = () => { setModal({ open: false, mode: 'add', data: null }); setCompanyOpt(null); };

  const handleSubmit = async (e) => {
    e.preventDefault(); setSaving(true);
    try {
      if (modal.mode === 'add') await api.post('/employees', form);
      else await api.put(`/employees/${modal.data._id}`, form);
      toast.success(modal.mode === 'add' ? 'Agent added!' : 'Updated!');
      close(); load();
    } catch (err) { toast.error(err.response?.data?.message || 'Error saving'); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Remove this employee?')) return;
    try { await api.delete(`/employees/${id}`); toast.success('Removed'); load(); }
    catch { toast.error('Delete failed'); }
  };

  const prevSearch = useRef(search);
  useEffect(() => { if (prevSearch.current !== search) { prevSearch.current = search; setPage(1); } });

  const columns = [
    { key: 'name', label: 'Name', render: r => <span className="font-semibold text-hajj-dark">{r.name}</span> },
    { key: 'phone', label: 'Phone' },
    { key: 'email', label: 'Email', render: r => r.email || <span className="text-gray-300">—</span> },
    { key: 'company', label: 'Company', render: r => r.company?.name || '—' },
    { key: 'status', label: 'Status', render: r => (
      <span className={`badge ${r.status === 'active' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-600'}`}>{r.status.charAt(0).toUpperCase() + r.status.slice(1)}</span>
    )},
    { key: 'actions', label: '', width: 80, render: r => (
      <div className="flex gap-1.5">
        <button onClick={() => openEdit(r)} className="w-8 h-8 rounded-lg bg-hajj-teal/10 text-teal-700 flex items-center justify-center hover:bg-hajj-teal/20 transition-colors"><LuPencil size={14} /></button>
        <button onClick={() => handleDelete(r._id)} className="w-8 h-8 rounded-lg bg-red-50 text-red-500 flex items-center justify-center hover:bg-red-100 transition-colors"><LuTrash2 size={14} /></button>
      </div>
    )},
  ];

  return (
    <div className="flex flex-col gap-5">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2.5 text-hajj-dark">
          <LuUsers className="text-xl sm:text-2xl" />
          <h1 className="text-xl sm:text-2xl font-extrabold">Hajj Agents</h1>
        </div>
        <button className="btn-primary text-sm" onClick={openAdd}><LuPlus size={15} /> Add Agent</button>
      </div>

      <div className="flex flex-col sm:flex-row sm:items-center gap-2.5">
        <div className="relative w-full sm:max-w-xs">
          <LuSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={15} />
          <input placeholder="Search by name or phone..." value={search} onChange={e => setSearch(e.target.value)} className="input-field pl-9 w-full" />
        </div>
        <span className="text-sm text-gray-400 sm:ml-auto">{pagination?.total ?? employees.length} agents</span>
      </div>

      <div className="card">
        <Table columns={columns} data={employees} loading={loading} emptyMessage="No agents found" />
        <Pagination page={page} total={pagination?.total || 0} pageSize={PAGE_SIZE} onPage={setPage} />
      </div>

      <Modal isOpen={modal.open} onClose={close} title={modal.mode === 'add' ? 'Add Agent' : 'Edit Agent'}>
        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Full Name *</label>
              <input value={form.name} onChange={set('name')} required className="input-field" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Phone *</label>
              <div className="flex w-full border border-gray-200 rounded-xl overflow-hidden focus-within:border-hajj-teal focus-within:ring-2 focus-within:ring-hajj-teal/10 transition-all duration-200">
                <span className="flex items-center px-3 bg-gray-50 border-r border-gray-200 text-sm text-gray-600 font-medium select-none whitespace-nowrap">+966</span>
                <input
                  type="tel"
                  inputMode="numeric"
                  value={form.phone.replace(/^\+966/, '')}
                  onChange={e => { const d = e.target.value.replace(/\D/g, ''); setForm(f => ({ ...f, phone: '+966' + d })); }}
                  required
                  maxLength={9}
                  placeholder="5XXXXXXXX"
                  className="flex-1 px-3 py-2.5 text-sm text-gray-800 bg-white outline-none"
                />
              </div>
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Email</label>
              <input type="email" value={form.email} onChange={set('email')} className="input-field" />
            </div>
            {isAdmin && (
              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Company *</label>
                <AsyncSelect
                  loadOptions={loadCompanies}
                  defaultOptions
                  cacheOptions
                  isClearable
                  placeholder="Search company..."
                  value={companyOpt}
                  onChange={opt => { setCompanyOpt(opt); setForm(f => ({ ...f, company: opt?.value || '' })); }}
                  styles={selectStyles}
                  menuPortalTarget={document.body}
                />
              </div>
            )}
          </div>
          <div className="flex justify-end gap-3 pt-2 border-t border-gray-100">
            <button type="button" className="btn-secondary" onClick={close}>Cancel</button>
            <button type="submit" className="btn-primary" disabled={saving}>
              {saving && <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />}
              {modal.mode === 'add' ? 'Add Agent' : 'Save Changes'}
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
